"use client";

import BottomCta from "@/components/BottomCta";
import Link from "next/link";
import { motion } from "framer-motion";
import { Calculator, BarChart3, FileText, Users, ShieldCheck, Check, CheckCircle2 } from "lucide-react";

const SERVICES = [
  { icon: FileText, title: "Bookkeeping", desc: "Daily financial records and cashflow control kept organized and accurate." },
  { icon: BarChart3, title: "Financial Reports", desc: "Monthly and quarterly financial insights to guide business decisions." },
  { icon: ShieldCheck, title: "VAT Services", desc: "VAT registration, returns, and full compliance support." },
  { icon: Users, title: "Payroll Processing", desc: "Accurate salary processing and staff records managed reliably." },
  { icon: Calculator, title: "Corporate Tax Support", desc: "Preparation, planning, and advisory for UAE corporate tax obligations." },
  { icon: FileText, title: "Audit Support", desc: "Documentation and preparation to ensure your accounts are audit-ready." },
];

const STEPS = [
  { num: "01", title: "Understanding Your Business", desc: "We assess your structure, transactions, and financial requirements in detail." },
  { num: "02", title: "System Setup", desc: "We organize your accounts with proper systems, controls, and categorization." },
  { num: "03", title: "Ongoing Management", desc: "We handle your records, filings, and reports accurately on a regular basis." },
  { num: "04", title: "Reporting & Insights", desc: "We provide clear financial reports to help you understand your position and plan ahead." },
];

const WHY_CHOOSE = [
  "Clear and easy-to-understand financial handling",
  "Accurate and timely reporting every period",
  "Strong knowledge of UAE VAT and tax laws",
  "Reliable and consistent support year-round",
  "Focus on helping your business grow",
];

export default function AccountingServicesPage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">

          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              VAT Registered · Audit Ready · UAE Compliant
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            Accounting Services <br />
            <span className="font-serif font-normal text-[#B8893A]">for UAE Companies</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            Bookkeeping, payroll, VAT, and audit-ready finance management crafted for UAE business operations. We keep your books clean, filings on time, and accounts ready for growth.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
          >
            <Link
              href="/contact"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Request Accounting
            </Link>
            <a
              href="https://wa.me/971586009983"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-[#B8893A] bg-transparent border-2 border-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#B8893A]/5 hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Book a Consultation
            </a>
          </motion.div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-2 gap-12 xl:gap-24">
          <div className="flex flex-col justify-center">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Accounting Matters</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Numbers that guide your next move</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              Strong financial management helps you understand where your business stands and where it is going. Without accurate records, decisions are guesswork.
            </p>
          </div>
          <div className="flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-8 md:pt-0 md:pl-12 xl:pl-24">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">What You Gain</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Control, clarity, and compliance</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              We handle the numbers so you can focus on running your business. From VAT to payroll, every financial obligation is managed accurately and on time.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">What We Offer</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Complete financial support for your business</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {SERVICES.map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={i}
                  className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-md flex flex-col justify-between"
                >
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-500 font-light text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Our Approach</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Simple. Structured. Reliable.</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 relative">
            {STEPS.map((step, i) => (
              <div key={i} className="relative group bg-white p-8 rounded-2xl border border-gray-100/80 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="text-6xl xl:text-7xl font-black text-gray-100 group-hover:text-[#B8893A]/10 transition-colors duration-300 mb-4">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold mb-2 tracking-tight">{step.title}</h3>
                <p className="text-gray-500 font-light text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH & WHY CHOOSE US */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-[#FAFAFA] border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Who We Work With</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Supporting businesses at every stage</h2>
            <p className="text-gray-500 font-light leading-relaxed">
              We don't just manage your accounts. We help you stay in control of your finances at every stage of growth.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              {[
                "Startups and new businesses",
                "Small and medium enterprises",
                "Growing companies expanding operations",
                "Entrepreneurs managing multiple activities",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-[#B8893A] shrink-0" />
                  <span className="text-sm font-semibold text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-4 block">Why Choose Us</span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">More than just accounting</h2>
            </div>
            {WHY_CHOOSE.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-gray-100 bg-white rounded-xl">
                <Check className="w-4 h-4 text-[#B8893A] shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCta
        title="Take control of your finances"
        subtitle="From bookkeeping to VAT filing and corporate tax, our experts ensure your finances are accurate, organized, and fully aligned with UAE regulations."
      />
    </main>
  );
}
